import React from 'react'
import { Link } from 'react-router-dom'
import { Wifi, Power, Smartphone, Check, AlertCircle, Clock, Settings } from 'lucide-react'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const SetupGuide = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container-padding py-12">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <Settings className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">FlowBeatIQ Setup Guide</h1>
          <p className="text-xl text-gray-600">
            Get your sensor up and running in under 10 minutes
          </p>
        </div>

        {/* Time Estimate */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">Estimated Time: 5-10 minutes</h3>
                <p className="text-blue-700 text-sm">No technical skills required!</p>
              </div>
            </div>
          </div>
        </div>

        {/* What You'll Need */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">What You'll Need</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Power className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Power Outlet</h3>
                  <p className="text-sm text-gray-600">Standard wall outlet in your business</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Wifi className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">WiFi Network</h3>
                  <p className="text-sm text-gray-600">Your business WiFi name and password</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Smartphone className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Phone or Laptop</h3>
                  <p className="text-sm text-gray-600">To configure the sensor</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Steps */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Setup Instructions</h2>

          {/* Step 1 */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full text-xl font-bold flex-shrink-0">
                1
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">Unbox and Power On</h3>
            </div>
            <div className="pl-16">
              <p className="text-gray-700 mb-4">
                Remove your FlowBeatIQ sensor from the box and plug it into a standard power outlet in your business location.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-700 mb-2"><strong>Best placement:</strong></p>
                <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                  <li>Near the center of your business (not in a back room or corner)</li>
                  <li>At least 3 feet off the ground</li>
                  <li>Away from metal objects or thick walls that might block WiFi signals</li>
                  <li>Within range of your WiFi network</li>
                </ul>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  <strong>LED Indicator:</strong> A small LED light on the sensor will blink when it powers on. Wait about 30 seconds for it to start up.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full text-xl font-bold flex-shrink-0">
                2
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">Connect to Sensor WiFi</h3>
            </div>
            <div className="pl-16">
              <p className="text-gray-700 mb-4">
                The sensor creates its own temporary WiFi network for setup. Connect to it from your phone or laptop.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-700 mb-3"><strong>On your phone or laptop:</strong></p>
                <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2">
                  <li>Open your WiFi settings</li>
                  <li>Look for a network named <code className="bg-gray-200 px-2 py-1 rounded font-mono">FlowBeatIQ-XXXX</code> (where XXXX is a unique ID)</li>
                  <li>Connect to this network (no password required)</li>
                  <li>Wait for the setup portal to open automatically</li>
                </ol>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  <strong>Setup portal not opening?</strong> Manually navigate to <code className="bg-blue-100 px-2 py-1 rounded font-mono">192.168.4.1</code> in your web browser.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full text-xl font-bold flex-shrink-0">
                3
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">Configure WiFi Settings</h3>
            </div>
            <div className="pl-16">
              <p className="text-gray-700 mb-4">
                The setup portal will show you a list of available WiFi networks. Connect your sensor to your business WiFi.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-700 mb-3"><strong>In the setup portal:</strong></p>
                <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2">
                  <li>Select your business WiFi network from the list</li>
                  <li>Enter your WiFi password</li>
                  <li>Click "Connect" or "Save"</li>
                  <li>Wait 30-60 seconds while the sensor connects</li>
                </ol>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 p-4 rounded-lg mb-4">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-900">
                  <strong>WiFi Requirements:</strong> The sensor only works with 2.4GHz WiFi networks (not 5GHz). Most business WiFi networks support both frequencies, so just connect to your regular network name.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-green-50 p-4 rounded-lg">
                <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-900">
                  <strong>Success:</strong> Once connected, the sensor will disappear from your available WiFi networks. This is normal! Reconnect your phone/laptop to your regular WiFi.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full text-xl font-bold flex-shrink-0">
                4
              </div>
              <h3 className="text-2xl font-semibold text-gray-900">Verify in Dashboard</h3>
            </div>
            <div className="pl-16">
              <p className="text-gray-700 mb-4">
                Log into your FlowBeatIQ dashboard to confirm your sensor is online and reporting data.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-2">
                  <li>Go to <a href="https://flowbeatiq.com/dashboard" className="text-blue-600 hover:underline">flowbeatiq.com/dashboard</a></li>
                  <li>Log in with your account credentials</li>
                  <li>Your sensor should appear within 5 minutes</li>
                  <li>You'll see "Learning your traffic patterns" during the first 7 days</li>
                </ol>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  <strong>Not seeing your sensor?</strong> Check that the LED is blinking regularly (indicating WiFi connection). If not, try unplugging the sensor for 30 seconds and plugging it back in.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Troubleshooting</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Sensor WiFi network not appearing</h3>
                <p className="text-gray-600 text-sm">
                  Make sure the sensor is plugged in and has been powered on for at least 30 seconds. Try moving closer to the sensor. If still not visible, unplug for 30 seconds and try again.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Can't connect to setup portal</h3>
                <p className="text-gray-600 text-sm">
                  Ensure you're connected to the FlowBeatIQ-XXXX network. Try manually navigating to <code className="bg-gray-100 px-2 py-1 rounded">192.168.4.1</code> in your browser. Disable cellular data on your phone if using a smartphone.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Sensor won't connect to WiFi</h3>
                <p className="text-gray-600 text-sm mb-2">
                  Double-check your WiFi password. Make sure you're selecting a 2.4GHz network (not 5GHz-only). Corporate networks with strict firewalls may require whitelisting—contact support for help.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Sensor not showing in dashboard</h3>
                <p className="text-gray-600 text-sm">
                  Wait up to 10 minutes after configuration. Check that the sensor LED is blinking steadily. Verify your WiFi network has internet access. If issues persist, contact support@flowbeatiq.com with your sensor ID.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Need to reset the sensor</h3>
                <p className="text-gray-600 text-sm">
                  Press and hold the small reset button (use a paperclip) for 10 seconds. The LED will blink rapidly to confirm. This will erase WiFi settings and you'll need to reconfigure.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Support CTA */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Still need help?</h2>
            <p className="text-gray-600 mb-6">
              Our support team is standing by to help you get set up
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@flowbeatiq.com"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
              >
                Email Support
              </a>
              <Link
                to="/help-center"
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-md font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-colors"
              >
                Visit Help Center
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default SetupGuide
